import {
    FormEvent,
    useCallback,
    useEffect,
    useRef,
} from "react";
import { useSelector } from "react-redux";

import {
    ArrowReload,
    ArrowTurnForward,
    Cancel,
    CancelCircle,
} from "@/assets/icons";
import {
    AlfredAvatar,
    AlfredContextIndicator,
    AlfredContextWarningBanner,
    AlfredFeedbackButtons,
    AlfredMessageRenderer,
    AlfredSuggestionChips,
} from "@/components";
import { constantsData } from "@/data";
import { SheetDirectionEnum } from "@/enums";
import {
    useAlfredContextUsage,
    useAlfredFeedback,
} from "@/hooks";
import type { AlfredFeedbackRating } from "@/hooks/alfred/use-alfred-feedback";
import { CurrentUserInterface } from "@/interfaces";
import { generateValidClassNameHandler } from "@/lib/utils";
import { useAlfredChatContext } from "@/providers/alfred/chat";
import { Button } from "@/ui/button";
import { Input } from "@/ui/input";
import {
    Sheet as SheetUI,
    SheetContent,
    SheetDescription,
    SheetHeader,
    SheetTitle,
} from "@/ui/sheet";

export const AlfredChatPanel = () => {
    const {
        error,
        handleInputChange,
        handleSubmit,
        input,
        isLoading,
        isOpen,
        messages,
        regenerate,
        setIsOpen,
        stop,
    } = useAlfredChatContext();

    const {
        getFeedback,
        isSubmitting: isFeedbackSubmitting,
        submitFeedback,
    } = useAlfredFeedback();

    const {
        dismissWarning,
        displayText,
        isNearLimit,
        maxOutputTokens,
        percentage,
        severity,
        usedTokens,
        warningDismissed,
    } = useAlfredContextUsage({ messages });

    const rootReducer = useSelector((state) => state as { user: CurrentUserInterface });
    const { user } = rootReducer;
    const { isAuthenticated } = user;

    const messagesEndRef = useRef<HTMLDivElement>(null);

    const handleFeedback = useCallback(
        (messageId: string, rating: AlfredFeedbackRating, comment?: string) => {
            submitFeedback({
                comment,
                message_id: messageId, // eslint-disable-line camelcase
                rating,
            });
        },
        [submitFeedback],
    );

    const scrollToBottom = useCallback(
        () => {
            messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
        },
        [],
    );

    useEffect(
        () => {
            scrollToBottom();
        },
        [messages, scrollToBottom],
    );

    const onSubmit = useCallback(
        (e: FormEvent) => {
            e.preventDefault();

            if (!input.trim() || isLoading) return;

            handleSubmit(e);
        },
        [handleSubmit, input, isLoading],
    );

    if (!isAuthenticated) return null;

    return (
        <>
            <Button
                aria-label="Open Alfred chat assistant"
                className="fixed bottom-6 right-6 z-[9999] size-auto cursor-pointer p-0 transition-transform hover:scale-105"
                type="button"
                variant="ghost"
                onClick={() => setIsOpen(true)}
            >
                <AlfredAvatar
                    isGenerating={isLoading}
                    size="lg"
                    showBadge
                />
            </Button>
            <SheetUI
                open={isOpen}
                onOpenChange={setIsOpen}
            >
                <SheetContent
                    className="flex w-full flex-col !gap-0 sm:max-w-[480px] md:max-w-[520px] lg:max-w-[600px]"
                    side={SheetDirectionEnum.RIGHT}
                >
                    <SheetHeader>
                        <SheetTitle className="flex items-center justify-between border-b pb-4">
                            <div className="flex items-center gap-3">
                                <AlfredAvatar size="sm" />
                                <span>{constantsData.titles.nayaAssistant}</span>
                            </div>
                            <div className="flex items-center gap-3">
                                {/* Context usage indicator */}
                                {messages.length > 0 && (
                                    <AlfredContextIndicator
                                        displayText={displayText}
                                        maxOutputTokens={maxOutputTokens}
                                        percentage={percentage}
                                        severity={severity}
                                        usedTokens={usedTokens}
                                    />
                                )}
                                <Button
                                    aria-label="Close chat"
                                    className="size-auto rounded-sm p-1 opacity-70 transition-opacity hover:opacity-100"
                                    size="icon"
                                    type="button"
                                    variant="ghost"
                                    onClick={() => setIsOpen(false)}
                                >
                                    <Cancel
                                        aria-hidden="true"
                                        className="size-4 text-gray-500 hover:text-gray-700"
                                    />
                                </Button>
                            </div>
                        </SheetTitle>
                        <SheetDescription className="sr-only">
                            Chat with Alfred, your NAYA Operations Support Agent
                        </SheetDescription>
                    </SheetHeader>
                    <div className="flex-1 overflow-y-auto p-4">
                        {messages.length === 0 && (
                            <div className="flex flex-col gap-4">
                                <p className="text-sm text-gray-600">
                                    {constantsData.msgs.alfred.chat.intro}
                                </p>
                                <AlfredSuggestionChips />
                            </div>
                        )}
                        <div className="space-y-4">
                            {messages.map((message) => {
                                const isUser = message.role === constantsData.msgs.alfred.messageRoles.user;
                                const isAssistant = message.role === constantsData.msgs.alfred.messageRoles.assistant;
                                return (
                                    <div
                                        key={message.id}
                                        className={generateValidClassNameHandler(
                                            "flex gap-3",
                                            isUser ? "flex-row-reverse" : "flex-row",
                                        )}
                                    >
                                        {/* Avatar for assistant messages */}
                                        {!isUser && (
                                            <AlfredAvatar
                                                className="mt-1 shrink-0"
                                                size="sm"
                                            />
                                        )}
                                        <div className="flex flex-col gap-1">
                                            <div
                                                className={generateValidClassNameHandler(
                                                    "rounded-lg p-3",
                                                    isUser
                                                        ? "bg-primary text-primary-foreground"
                                                        : "bg-gray-100",
                                                )}
                                            >
                                                <AlfredMessageRenderer
                                                    isUser={isUser}
                                                    message={message}
                                                />
                                            </div>
                                            {/* Feedback buttons for assistant messages */}
                                            {isAssistant && !isLoading && (
                                                <AlfredFeedbackButtons
                                                    currentFeedback={getFeedback(message.id)}
                                                    disabled={isFeedbackSubmitting}
                                                    messageId={message.id}
                                                    onFeedback={handleFeedback}
                                                />
                                            )}
                                        </div>
                                    </div>
                                );
                            })}
                            {isLoading && (
                                <div className="flex gap-3">
                                    <AlfredAvatar
                                        className="mt-1 shrink-0"
                                        size="sm"
                                        isGenerating
                                    />
                                    <div className="flex flex-col gap-2">
                                        <div className="flex items-center gap-2 rounded-lg bg-gray-100 p-3 text-gray-600">
                                            <ArrowReload className="size-4 animate-spin" />
                                            <span className="text-sm">{constantsData.msgs.alfred.analyzing}</span>
                                        </div>
                                        <Button
                                            aria-label="Stop generating"
                                            className="w-fit"
                                            size="sm"
                                            variant="outline"
                                            onClick={stop}
                                        >
                                            <CancelCircle className="mr-1.5 size-3.5" />
                                            Stop
                                        </Button>
                                    </div>
                                </div>
                            )}
                            {!isLoading && messages.length > 0 && messages[messages.length - 1]?.role === constantsData.msgs.alfred.messageRoles.assistant && (
                                <div className="flex justify-start pl-10">
                                    <Button
                                        aria-label="Regenerate response"
                                        size="sm"
                                        variant="ghost"
                                        onClick={regenerate}
                                    >
                                        <ArrowReload className="mr-1.5 size-3.5" />
                                        Regenerate
                                    </Button>
                                </div>
                            )}
                            {error && (
                                <div className="mr-8 rounded-lg bg-red-50 p-3 text-red-600">
                                    <p className="text-sm">
                                        {constantsData.msgs.alfred.explanationFailed}
                                        {" "}
                                        {error.message}
                                    </p>
                                </div>
                            )}

                            <div ref={messagesEndRef} />
                        </div>
                    </div>

                    {/* Context warning banner - shown when near limit */}
                    {isNearLimit && !warningDismissed && (
                        <div className="px-4 pt-2">
                            <AlfredContextWarningBanner
                                percentage={percentage}
                                onDismiss={dismissWarning}
                            />
                        </div>
                    )}

                    <form
                        className="flex gap-2 border-t p-4"
                        onSubmit={onSubmit}
                    >
                        <Input
                            className="flex-1"
                            disabled={isLoading}
                            placeholder={constantsData.msgs.alfred.chat.placeholder}
                            value={input}
                            onChange={handleInputChange}
                        />
                        <Button
                            disabled={isLoading || !input.trim()}
                            size="icon"
                            type="submit"
                        >
                            <ArrowTurnForward className="size-4" />
                        </Button>
                    </form>
                </SheetContent>
            </SheetUI>
        </>
    );
};
